import React, { useState, useEffect } from 'react';
import { CreditCard, User, Home, Image } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import CustomSelect from '../common/CustomSelect';
import TextInput from '../common/TextInput';
import TextArea from '../common/TextArea';
import { FormGroup, FormRow } from '../common/FormComponents';
import { getAccountTypeLabel } from '../../utils/formatters';
import CardArtPicker, { CardImagePreview } from '../common/CardArtPicker';

/**
 * PaymentAccountModal - Create/Edit payment accounts
 * @param {boolean} forceOwnership - If set to 'personal' or 'household', locks the ownership selection
 */
function PaymentAccountModal({ isOpen, onClose, onSubmit, account, saving: externalSaving, forceOwnership }) {
  // Get current user to determine household status
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isInHousehold = !!currentUser.household_id;
  const isManager = currentUser.household_role === 'manager';

  // Determine if ownership is locked based on forceOwnership prop
  const ownershipLocked = forceOwnership === 'personal' || forceOwnership === 'household';
  const forcedToHousehold = forceOwnership === 'household';

  const [formData, setFormData] = useState({
    accountName: '',
    accountType: '',
    lastFour: '',
    institutionName: '',
    nameOnCard: '',
    notes: '',
    cardImage: null,
    accountNameManuallyEdited: false,
    isHouseholdAccount: false
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCardArtPicker, setShowCardArtPicker] = useState(false);
  const [returningFromCardArt, setReturningFromCardArt] = useState(false);

  const isSaving = externalSaving !== undefined ? externalSaving : saving;

  // When card art picker closes, suppress backdrop clicks on parent modal
  // to prevent the click from propagating through and closing this modal.
  // Uses a longer guard period because requestAnimationFrame in CardArtPicker
  // can still allow click events to reach the backdrop.
  const handleCardArtClose = () => {
    setReturningFromCardArt(true);
    setShowCardArtPicker(false);
    setTimeout(() => setReturningFromCardArt(false), 500);
  };

  useEffect(() => {
    if (account) {
      setFormData({
        accountName: account.account_name || '',
        accountType: account.account_type || '',
        lastFour: account.last_four || account.last_four_digits || '',
        institutionName: account.institution_name || '',
        nameOnCard: account.name_on_card || '',
        notes: account.notes || '',
        cardImage: null,
        accountNameManuallyEdited: false,
        isHouseholdAccount: !!account.household_id
      });
      setImagePreview(account.card_image || null);
    } else {
      // For new accounts, use forceOwnership if provided
      setFormData({
        accountName: '',
        accountType: '',
        lastFour: '',
        institutionName: '',
        nameOnCard: '',
        notes: '',
        cardImage: null,
        accountNameManuallyEdited: false,
        isHouseholdAccount: forcedToHousehold
      });
      setImagePreview(null);
    }
    setError('');
  }, [account, isOpen, forcedToHousehold]);

  const generateAccountName = (data) => {
    if (data.accountType === 'cash') return 'Cash';
    
    const parts = [];
    if (data.institutionName) parts.push(data.institutionName);
    if (data.accountType) parts.push(getAccountTypeLabel(data.accountType));
    if (data.lastFour) parts.push(`(${data.lastFour})`);
    
    return parts.join(' ');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'accountName') {
      setFormData({ ...formData, [name]: value, accountNameManuallyEdited: true });
    } else {
      const newFormData = { ...formData, [name]: value };
      const generatedName = generateAccountName(newFormData);
      if (generatedName && !formData.accountNameManuallyEdited) {
        setFormData({ ...newFormData, accountName: generatedName });
      } else {
        setFormData(newFormData);
      }
    }
  };

  const handleCardArtSelect = (selection) => {
    if (selection.type === 'generated' || selection.type === 'uploaded') {
      // User generated a custom card or uploaded an image
      setImagePreview(selection.dataUrl);
      setFormData({ ...formData, cardImage: null });
    } else if (selection.type === 'custom') {
      // User entered custom URL
      setImagePreview(selection.url);
      setFormData({ ...formData, cardImage: null });
    } else if (selection.type === 'clear') {
      // User cleared the image
      setImagePreview(null);
      setFormData({ ...formData, cardImage: null });
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, cardImage: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    if (!formData.accountName?.trim()) {
      setError('Account name is required');
      return;
    }
    if (!formData.accountType) {
      setError('Account type is required');
      return;
    }

    if (externalSaving === undefined) setSaving(true);

    try {
      await onSubmit({
        accountName: formData.accountName.trim(),
        accountType: formData.accountType,
        lastFour: formData.lastFour?.trim() || null,
        institutionName: formData.institutionName?.trim() || null,
        nameOnCard: formData.nameOnCard?.trim() || null,
        notes: formData.notes?.trim() || null,
        cardImage: imagePreview || null,
        isHouseholdAccount: formData.isHouseholdAccount
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save payment account');
      throw err;
    } finally {
      if (externalSaving === undefined) setSaving(false);
    }
  };

  // Determine if we should show card-related fields (credit cards, checking accounts with cards, etc.)
  const showCardFields = formData.accountType && formData.accountType !== 'cash';
  const showNameOnCard = ['credit_card', 'checking'].includes(formData.accountType);

  const footer = (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
        Cancel
      </button>
      <button type="submit" form="payment-account-form" className="btn btn-primary" disabled={isSaving}>
        {isSaving ? 'Saving...' : account ? 'Update Account' : 'Add Account'}
      </button>
    </>
  );

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title={account ? 'Edit Payment Account' : 'Add Payment Account'}
        icon={<CreditCard size={24} />}
        footer={footer}
        disabled={isSaving}
        hidden={showCardArtPicker}
        closeOnBackdrop={!returningFromCardArt}
      >
        <form id="payment-account-form" onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            {/* Personal vs Household Toggle - only show if user is in a household */}
            {isInHousehold && (
              <FormGroup label="Account Ownership">
                {ownershipLocked ? (
                  // Show locked state when forceOwnership is set - simplified display
                  <div className="ownership-locked-info">
                    <div className={`ownership-locked-badge ${forcedToHousehold ? 'household' : 'personal'}`}>
                      {forcedToHousehold ? <Home size={16} /> : <User size={16} />}
                      <span>{forcedToHousehold ? 'Household Account' : 'Personal Account'}</span>
                    </div>
                    <p className="ownership-locked-hint">
                      {forcedToHousehold 
                        ? 'Creating for a household bill - will be shared with all members'
                        : 'Creating for a personal bill - only you can see this account'
                      }
                    </p>
                  </div>
                ) : (
                  // Show normal toggle when not locked
                  <div className="ownership-toggle">
                    <button
                      type="button"
                      className={`ownership-option ${!formData.isHouseholdAccount ? 'active' : ''}`}
                      onClick={() => setFormData({ ...formData, isHouseholdAccount: false })}
                      disabled={isSaving}
                    >
                      <User size={18} />
                      <div>
                        <span className="ownership-label">Personal</span>
                        <span className="ownership-desc">Only you can see this account</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`ownership-option ${formData.isHouseholdAccount ? 'active' : ''} ${!isManager ? 'disabled' : ''}`}
                      onClick={() => isManager && setFormData({ ...formData, isHouseholdAccount: true })}
                      disabled={isSaving || !isManager}
                      title={!isManager ? 'Only managers can create household accounts' : ''}
                    >
                      <Home size={18} />
                      <div>
                        <span className="ownership-label">Household</span>
                        <span className="ownership-desc">
                          {isManager ? 'Shared with all members' : 'Manager role required'}
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </FormGroup>
            )}

            <FormGroup label="Account Type" required htmlFor="accountType">
              <CustomSelect
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                disabled={isSaving}
                placeholder="Select account type..."
                options={[
                  { value: 'checking', label: 'Checking', icon: '🏦' },
                  { value: 'savings', label: 'Savings', icon: '💰' },
                  { value: 'credit_card', label: 'Credit Card', icon: '💳' },
                  { value: 'cash', label: 'Cash', icon: '💵' },
                  { value: 'other', label: 'Other', icon: '💼' }
                ]}
              />
            </FormGroup>

            {showCardFields && (
              <FormRow>
                <FormGroup label="Institution Name" htmlFor="institutionName">
                  <TextInput
                    id="institutionName"
                    name="institutionName"
                    value={formData.institutionName}
                    onChange={handleChange}
                    disabled={isSaving}
                    placeholder="e.g., Chase Bank"
                  />
                </FormGroup>

                <FormGroup label="Last 4 Digits" htmlFor="lastFour">
                  <TextInput
                    id="lastFour"
                    name="lastFour"
                    value={formData.lastFour}
                    onChange={handleChange}
                    maxLength={4}
                    disabled={isSaving}
                    placeholder="1234"
                  />
                </FormGroup>
              </FormRow>
            )}

            {/* Name on Card - only for credit cards and checking accounts */}
            {showNameOnCard && (
              <FormGroup label="Name on Card" htmlFor="nameOnCard">
                <TextInput
                  id="nameOnCard"
                  name="nameOnCard"
                  value={formData.nameOnCard}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="e.g., John Smith"
                />
              </FormGroup>
            )}

            {formData.accountType && (
              <FormGroup label="Account Name" required htmlFor="accountName">
                <TextInput
                  id="accountName"
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                  placeholder="Account name"
                />
              </FormGroup>
            )}

            {formData.accountType && (
              <FormGroup label="Card Image (Optional)">
                <div className="image-upload-area">
                  {imagePreview ? (
                    <CardImagePreview
                      imageUrl={imagePreview}
                      onEdit={() => setShowCardArtPicker(true)}
                      onRemove={removeImage}
                      disabled={isSaving}
                    />
                  ) : (
                    <button
                      type="button"
                      className="add-card-image-btn"
                      onClick={() => setShowCardArtPicker(true)}
                      disabled={isSaving}
                    >
                      <Image size={20} />
                      <span>Add Card Image</span>
                    </button>
                  )}
                </div>
              </FormGroup>
            )}

            {formData.accountType && (
              <FormGroup label="Notes" htmlFor="notes">
                <TextArea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  disabled={isSaving}
                  rows={2}
                  placeholder="Optional notes about this account"
                />
              </FormGroup>
            )}
          </div>
        </form>
      </BaseModal>

      {/* Card Art Picker Modal */}
      <CardArtPicker
        isOpen={showCardArtPicker}
        onClose={handleCardArtClose}
        onSelect={handleCardArtSelect}
        currentImage={imagePreview}
        institutionHint={formData.institutionName}
        lastFourHint={formData.lastFour}
        nameOnCardHint={formData.nameOnCard}
        accountType={formData.accountType}
      />
    </>
  );
}

export default PaymentAccountModal;
